import Vue from 'vue'
import VueRouter from 'vue-router'

import * as guards from '@/router/guards'
import * as titleManager from '@/utils/title-manager'
import i18n from '@/plugins/i18n'

import LayoutEmpty from '@/layouts/LayoutEmpty.vue'
import LayoutPublic from '@/layouts/LayoutPublic.vue'
import LayoutHub from '@/layouts/LayoutHub.vue'

import LandingPage from '@/pages/LandingPage.vue'
import TestPage from '@/pages/TestPage.vue'
import ProfilePage from '@/pages/ProfilePage.vue'
// import TutorialPage from '@/pages/TutorialPage.vue'
// import RebornPage from '@/pages/RebornPage.vue'
// import PublicOfferPage from '@/pages/PublicOfferPage.vue'
import PublicTermsOfServicesPage from '@/pages/PublicTermsOfServicesPage.vue'
import PublicPrivacyPolicyPage from '@/pages/PublicPrivacyPolicyPage.vue'
import SigninPage from '@/pages/SigninPage.vue'
import SigninSuccessPage from '@/pages/SigninSuccessPage.vue'
import OnboardingPage from '@/pages/OnboardingPage.vue'
import DemoPage from '@/pages/DemoPage.vue'

import HubSectionsPage from '@/pages/HubSectionsPage.vue'
import HubExercisesPage from '@/pages/HubExercisesPage.vue'
import HubPaywallPage from '@/pages/HubPaywallPage.vue'
import HubPaymentHistoryPage from '@/pages/HubPaymentHistory.vue'

// import TopicsPage from '@/pages/TopicsPage.vue'
// import SectionsPage from '@/pages/SectionsPage.vue'
// import ExercisesPage from '@/pages/ExercisesPage.vue'

const originalPush = VueRouter.prototype.push
const originalResolve = VueRouter.prototype.resolve

VueRouter.prototype.push = function push(to) {
  return originalPush.call(this, to).catch(error => {
    if (error.name === 'NavigationDuplicated') {
      console.warn('Navigation duplicated')
    } else if (error.message?.includes('Redirected when going from')) {
      console.warn('Route path dynamic updated')
    } else {
      console.warn(error)
    }
  })
}

VueRouter.prototype.open = function (to) {
  const routeData = originalResolve.call(this, to)

  window.open(routeData.href, '_blank')
}

const routes = [
  {
    path: '/',
    name: 'home',
    component: LandingPage,
    // beforeEnter: guards?.multiple([guards.onlyNotAuthorized]),
    meta: { title: 'English Grammar Exercises with Explanations' }
  },

  {
    path: '/grammar',
    name: 'grammar',
    component: () => import('@/pages/GrammarPage.vue'),
    meta: { title: 'Grammar' }
  },

  {
    path: '/test',
    name: 'test',
    component: TestPage,
    meta: { title: 'Test' }
  },

  // {
  //   path: '/tutorial',
  //   name: 'tutorial',
  //   component: TutorialPage,
  //   meta: { title: 'Tutorial' }
  // },

  {
    path: '/signin',
    name: 'signin',
    component: SigninPage,
    beforeEnter: guards?.multiple([guards.onlyNotAuthorized]),
    meta: { title: 'Sign In' }
  },

  // {
  //   path: '/signin/plan/:plan',
  //   name: 'signin.plan',
  //   component: SigninPage,
  //   beforeEnter: guards?.multiple([guards.onlyNotAuthorized]),
  //   meta: { title: 'Sign In with Plan' }
  // },

  {
    path: '/signin/success/:plan?',
    name: 'signin.success',
    component: SigninSuccessPage,
    beforeEnter: guards?.multiple([
      guards.onlyAuthorized,
      guards.onlyFromSigninPages
    ]),
    meta: { title: 'Sign In Success' }
  },

  {
    path: '/onboarding',
    name: 'onboarding',
    component: OnboardingPage,
    beforeEnter: guards?.multiple([
      guards.onlyAuthorized,
      guards.onlyNotOnboarded
    ]),
    meta: { title: 'Onboarding' }
  },

  {
    path: '/demo',
    component: LayoutEmpty,
    meta: { title: 'Demo' },
    children: [
      {
        name: 'demo',
        path: '',
        component: DemoPage
      }
    ]
  },

  {
    path: '/public',
    name: 'public',
    redirect: { name: 'public.terms-of-services' },
    component: LayoutPublic,
    children: [
      {
        path: 'privacy-policy',
        name: 'public.privacy-policy',
        component: PublicPrivacyPolicyPage,
        meta: { title: 'Privacy Policy' }
      },
      {
        path: 'terms-of-services',
        name: 'public.terms-of-services',
        component: PublicTermsOfServicesPage,
        meta: { title: 'Terms of Services' }
      },
      {
        path: 'tariffs',
        name: 'public.tariffs',
        redirect: { name: 'public.terms-of-services' }
      },
      {
        path: 'payment-success',
        name: 'public.payment-success',
        component: () => import('@/pages/PublicPaymentSuccessPage.vue'),
        meta: { title: 'Payment Success' },
        beforeEnter: guards?.multiple([
          guards?.onlyAuthorized,
          guards.onlyFromPaymentStatusPage
        ])
      },
      {
        path: 'payment-error/:orderId',
        name: 'public.payment-error',
        component: () => import('@/pages/PublicPaymentErrorPage.vue'),
        meta: { title: 'Payment Error' },
        beforeEnter: guards?.multiple([guards.onlyFromPaymentStatusPage])
      },
      {
        path: 'payment-status/:orderId',
        name: 'public.payment-status',
        component: () => import('@/pages/PublicPaymentStatusPage.vue'),
        meta: { title: 'Payment Status Check' },
        beforeEnter: guards?.multiple([guards?.onlyAuthorized])
      }
    ]
  },

  {
    path: '/profile',
    name: 'profile',
    beforeEnter: guards?.multiple([guards.onlyAuthorized]),
    component: ProfilePage,
    meta: { title: 'Profile' }
  },

  {
    path: '/hub',
    name: 'hub',
    redirect: { name: 'hub.sections' },
    beforeEnter: guards?.multiple([
      guards.onlyAuthorized,
      guards.onlyOnboarded
    ]),
    component: LayoutHub,
    children: [
      {
        name: 'hub.paywall',
        path: 'paywall',
        component: HubPaywallPage,
        meta: { title: 'Paywall' },
        beforeEnter: guards?.multiple([guards.onlyNotPremium])
      },
      {
        path: '',
        component: LayoutEmpty,
        beforeEnter: guards?.multiple([guards.onlyPremium]),
        children: [
          {
            name: 'hub.sections',
            path: 'sections',
            component: HubSectionsPage,
            meta: { title: 'pages.sections' }
          },
          {
            name: 'hub.exercises',
            path: 'sections/:sectionSlug/:topicSlug',
            component: HubExercisesPage
          },
          {
            name: 'hub.payment-history',
            path: 'payment-history',
            component: HubPaymentHistoryPage,
            meta: { title: 'Payment History' }
          }
        ]
      }

      // {
      //   name: 'hub.exercises',
      //   path: 'sections/:sectionSlug/:topicSlug',
      //   component: HubExercisesPage,
      //   beforeEnter: guards?.multiple([guards.checkTopicAccess])
      // },
    ]
  },

  {
    path: '/admin',
    name: 'admin',
    component: () => import('@/pages/AdminPage.vue'),
    beforeEnter: guards?.multiple([guards.onlyAdmin]),
    meta: { title: 'Admin' }
  },

  {
    path: '/playground',
    component: LayoutEmpty,
    children: [
      {
        name: 'playground',
        path: '',
        component: () => import('@/pages/PlaygroundPage.vue')
      },
      {
        name: 'playground.exercises',
        path: ':sectionSlug/:topicSlug',
        component: () => import('@/pages/HubExercisesPage.vue')
      }
    ]
  },

  {
    path: '*',
    redirect: { name: 'home' }
  }
]

const router = new VueRouter({
  mode: 'history',
  base: import.meta.env.BASE_URL,
  routes,
  scrollBehavior: () => ({ x: 0, y: 0 })
})

router.afterEach(to => {
  console.log('Route changed to:', to.path)

  if (to.meta?.title || to.meta?.title === '') {
    const title = to.meta.title ? i18n.t(to.meta.title) : ''
    titleManager.setTitle([title])
  }
})

Vue.use(VueRouter)

export default router
